import { type ReactNode, type RefObject } from "react";

import {
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

export interface CommandMenuItem {
  id: string;
  label: string;
  description?: string;
  keywords?: string[];
  icon?: ReactNode;
  run(): void;
}

export interface CommandMenuGroup {
  id: string;
  label: string;
  items: CommandMenuItem[];
}

interface ProjectCommandMenuProps {
  groups: CommandMenuGroup[];
  inputRef?: RefObject<HTMLInputElement | null>;
  placeholder?: string;
  emptyMessage?: string;
  onItemSelected?: () => void;
  listClassName?: string;
}

export function ProjectCommandMenu({
  groups,
  inputRef,
  placeholder = "Search...",
  emptyMessage = "No results found.",
  onItemSelected,
  listClassName,
}: ProjectCommandMenuProps) {
  const nonEmptyGroups = groups.filter((group) => group.items.length > 0);

  return (
    <>
      <CommandInput ref={inputRef} placeholder={placeholder} />
      <CommandList className={cn("flex-1", listClassName)}>
        <CommandEmpty>{emptyMessage}</CommandEmpty>
        {nonEmptyGroups.map((group) => (
          <CommandGroup key={group.id} heading={group.label}>
            {group.items.map((item) => (
              <CommandItem
                key={item.id}
                value={`${item.label} ${item.keywords?.join(" ") ?? ""}`.trim()}
                onSelect={() => {
                  item.run();
                  onItemSelected?.();
                }}
              >
                {item.icon ? (
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-muted">
                    {item.icon}
                  </span>
                ) : null}
                <span className="flex flex-1 flex-col text-left">
                  <span className="font-medium leading-none">{item.label}</span>
                  {item.description ? (
                    <span className="mt-1 text-xs text-muted-foreground">
                      {item.description}
                    </span>
                  ) : null}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </>
  );
}
